import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';

import { EnvironmentService, ErableHttpService, IdUtils, RequestOptions } from '@ecm/consumer';

import {
    AddressBusinessModel,
    AddressResultBusinessModel,
    FormattedPostalAddressBusinessModel,
} from '../business/address/address-format';

export enum AddressLevelEnum {
    NUMERO = 'numero',
    VOIE = 'voie',
    VILLE = 'ville',
}

@Injectable({
    providedIn: 'root',
})
export class AddressService {
    /**
     * tracking bco partner name
     * @type {string}
     */
    private readonly ZDR_PARTNER_NAME: string = 'ZDR';

    /**
     * tracking bco feature name
     * @type {string}
     */
    private readonly FEATURE_NAME: string = 'ECM_MATERIAL';

    /**
     * tracking bco page id
     * @type {string}
     */
    private readonly pageId: string;

    /**
     * Object formatted postal address
     */
    public static addressObjectFormatter(
        id: number,
        address: string,
        postalCode: string,
        cityName: string,
    ): AddressBusinessModel {
        return {
            id,
            postalCode,
            cityName,
            formattedPostalAddress: [
                {
                    lineNumber: '4',
                    text: address.toUpperCase(),
                },
                {
                    lineNumber: '6',
                    text: postalCode + ' ' + cityName.toUpperCase(),
                },
            ],
        };
    }

    public constructor(
        private environmentService: EnvironmentService,
        private http: ErableHttpService,
    ) {
        this.pageId = IdUtils.generateRandomId();
    }

    /**
     * Extract specified postal line
     * @param {FormattedPostalAddressBusinessModel[]} address Formatted postal address
     * @param {number} lineNumber
     * @returns {string}
     */
    public static getAddressLine(address: FormattedPostalAddressBusinessModel[], lineNumber: number): string {
        if (!AddressService.hasAddressLine(address, lineNumber)) {
            return '';
        }
        const filteredAddress = address.filter(
            (line: FormattedPostalAddressBusinessModel) => line.lineNumber === lineNumber.toString(),
        );
        return `${filteredAddress[0]?.text} `;
    }

    /**
     * Check if specified address line exist
     * @param {FormattedPostalAddressBusinessModel[]} address Formatted postal address
     * @param {number} lineNumber
     * @returns {boolean}
     */
    public static hasAddressLine(address: FormattedPostalAddressBusinessModel[], lineNumber: number): boolean {
        return address.some((line: FormattedPostalAddressBusinessModel) => line.lineNumber === lineNumber.toString());
    }

    private static getHttpParams(text: string, level?: AddressLevelEnum): HttpParams {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('mode', 'fullText').set('fullText', text);
        return level ? httpParams.set('addressLevel', level) : httpParams;
    }

    public addressAutocomplete = (
        text$: Observable<string>,
        options?: RequestOptions,
        params?: { level?: AddressLevelEnum | undefined; size?: number | undefined },
    ): Observable<AddressBusinessModel[]> => {
        return text$.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((term) =>
                term.length < (params?.size || 4)
                    ? of([])
                    : this.searchAddress(term, options, params?.level).pipe(catchError(() => of([]))),
            ),
        );
    };

    /**
     * Inline formatted postal address
     * @param {AddressBusinessModel} address
     * @returns {string}
     */
    public addressInlineFormatter(address: AddressBusinessModel): string {
        return `${AddressService.getAddressLine(address?.formattedPostalAddress, 4)}${AddressService.getAddressLine(
            address?.formattedPostalAddress,
            5,
        )}${AddressService.getAddressLine(address?.formattedPostalAddress, 6)}`.trim();
    }

    /**
     * Search address with GPS coords
     * @param {number} geoCodeX
     * @param {number} geoCodeY
     * @returns {AddressBusinessModel[]}
     */
    public searchGpsCoordsAddress(geoCodeX: number, geoCodeY: number): Observable<AddressBusinessModel[]> {
        if (!geoCodeX || !geoCodeY) {
            return of([]);
        }
        const url = `${this.environmentService.instant(
            'API.ADDRESS_URL',
        )}addresses?mode=geoCoordinates&geoCodeX=${geoCodeX}&geoCodeY=${geoCodeY}`;

        return this.http
            .get(url, {
                feature: this.FEATURE_NAME,
                pageId: this.pageId,
                functionality: 'API_address',
                partner: this.ZDR_PARTNER_NAME,
                service: 'address',
            })
            .pipe(map((result: { addresses: AddressBusinessModel[] }) => result.addresses));
    }

    public validateAddress(
        street: string,
        postalCode: string,
        city: string,
        options?: RequestOptions,
    ): Observable<AddressResultBusinessModel> {
        const url = `${this.environmentService.instant(
            'API.ADDRESS_URL',
        )}addresses?mode=validateAddress&line4AFNOR=${street}&line6AFNOR=${postalCode} ${city}`;
        return this.getAddressResult(url, options);
    }

    private searchAddress(
        text: string,
        options?: RequestOptions,
        level?: AddressLevelEnum,
    ): Observable<AddressBusinessModel[]> {
        if (text === '') {
            return of([]);
        }
        const url = `${this.environmentService.instant('API.ADDRESS_URL')}addresses?${AddressService.getHttpParams(
            text,
            level,
        )}`;
        return this.getAddresses(url, options);
    }

    private getAddresses(url: string, options: RequestOptions): Observable<AddressBusinessModel[]> {
        return this.getAddressResult(url, options).pipe(map((result: AddressResultBusinessModel) => result.addresses));
    }

    private getAddressResult(url: string, options: RequestOptions): Observable<AddressResultBusinessModel> {
        return this.http.get(url, {
            feature: this.FEATURE_NAME,
            pageId: this.pageId,
            functionality: 'API_address',
            partner: this.ZDR_PARTNER_NAME,
            service: 'address',
            ...(options || {}),
        });
    }
}
